import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, LinkIcon, useConfirm, useToast } from "@signet/ui";
import { POSTING_DAYS } from "../../shared/lifecycle.ts";
import { shortDate, stockKeys, tickStock, unlinkStock, untickStock, type Stock } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The day track: one box for each warm-up day, one for each of the three
   posting days, then the link. Posters tick the next box when that day's
   work is done (Jay, 2026-10-07: "I want them to literally tick off each
   day"), and can untick any ticked box; days go in order, so unticking one
   unticks the days after it too, and says so first. The link box opens the
   link pop-up (link.tsx); unticking it takes the flag off first, then the link.

   An account that came off the sheet already live is past all of it, so
   every box shows ticked (Jay, 2026-10-15: "all accounts that are active
   should have 100% everything ticked"). Those boxes have no tick behind them
   in the database, so they cannot be unticked.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

/** done and on differ: ticks brought in off the sheet are done with no date. */
type Box = { key: string; phase: "warmup" | "posting"; day: number; label: string; done: boolean; on: string | null };

/**
 * Ticks the next day: shared by the day track and the one-line rows on a
 * poster's phones, so both say the same thing and offer the same Undo.
 */
export function useDayTick(a: Stock) {
  const client = useQueryClient();
  const toast = useToast();
  const refresh = () => client.invalidateQueries({ queryKey: stockKeys.all });
  return useMutation({
    mutationFn: () => tickStock(a.id),
    onSuccess: (r) => {
      void refresh();
      const done = r.phase === "warmup" && r.day === a.warmup ? "Warm-up finished. Posting starts next." : r.phase === "posting" && r.day === POSTING_DAYS ? "Posting days done. Add the link next." : undefined;
      toast.push({
        tone: "good",
        title: `@${a.username}: ${r.phase === "warmup" ? `warm-up day ${r.day}` : `posting day ${r.day}`} ticked`,
        body: done,
        duration: 6000,
        // Called straight, not through the hook: the toast can outlive this row.
        action: { label: "Undo", onSelect: () => void untickStock(a.id, r).then(refresh, (e) => toast.error("Couldn't undo it", messageOf(e))) },
      });
    },
    onError: (e) => toast.error("Couldn't tick it", messageOf(e)),
  });

}

/** With readOnly (someone else's phone) the boxes show where it is and cannot be pressed. */
export function DayTrack({ a, onLink, readOnly = false }: { a: Stock; onLink: () => void; readOnly?: boolean }) {
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const refresh = () => client.invalidateQueries({ queryKey: stockKeys.all });

  const untick = useMutation({
    mutationFn: (box: Box) => untickStock(a.id, { phase: box.phase, day: box.day }),
    onSuccess: refresh,
    onError: (e) => toast.error("Couldn't untick it", messageOf(e)),
  });
  const unlink = useMutation({ mutationFn: () => unlinkStock(a.id), onSuccess: refresh, onError: (e) => toast.error("Couldn't untick it", messageOf(e)) });
  const tick = useDayTick(a);

  const live = Boolean(a.importedActive);
  const warmup: Box[] = Array.from({ length: a.warmup }, (_, i) => ({ key: `w${i}`, phase: "warmup", day: i + 1, label: `Day ${i + 1}`, done: live || i < a.ticks.warmup.length, on: a.ticks.warmup[i] ?? null }));
  const posting: Box[] = Array.from({ length: POSTING_DAYS }, (_, i) => ({ key: `p${i}`, phase: "posting", day: i + 1, label: `Post ${i + 1}`, done: live || i < a.ticks.posting.length, on: a.ticks.posting[i] ?? null }));
  const all = [...warmup, ...posting];
  const next = all.find((box) => !box.done)?.key ?? null;
  const linkReady = next === null && !a.linkAddedOn && !live;
  const busy = readOnly || tick.isPending || untick.isPending || unlink.isPending;

  const askUntick = async (b: Box) => {
    if (a.linkAddedOn) {
      toast.error("Untick the link first", "The link went in because these days were done.");
      return;
    }
    const after = all.slice(all.findIndex((x) => x.key === b.key) + 1).filter((x) => x.done);
    if (after.length && !(await confirm({ title: `Untick ${b.label}?`, body: `Days go in order, so ${after.map((x) => x.label).join(", ")} ${after.length === 1 ? "comes" : "come"} off too.`, confirmLabel: "Untick" }))) return;
    untick.mutate(b);
  };

  const box = (b: Box) => {
    if (b.done && live) {
      return (
        <li key={b.key} className="igs-day done" aria-label={`${b.label}, ticked`} title="Live when it came off the sheet">
          <span className="igs-day-mark"><CheckIcon size={16} /></span>
          <span className="igs-day-label">{b.label}</span>
          <span className="igs-day-date">&nbsp;</span>
        </li>
      );
    }
    if (b.done) {
      return (
        <li key={b.key}>
          <button type="button" className="igs-day done" disabled={busy} onClick={() => void askUntick(b)} aria-label={b.on ? `${b.label}, ticked ${shortDate(b.on)}. Untick it` : `${b.label}, ticked. Untick it`} title="Untick">
            <span className="igs-day-mark"><CheckIcon size={16} /></span>
            <span className="igs-day-label">{b.label}</span>
            <span className="igs-day-date">{b.on ? shortDate(b.on) : "\u00a0"}</span>
          </button>
        </li>
      );
    }
    if (b.key === next) {
      return (
        <li key={b.key}>
          <button type="button" className="igs-day next" disabled={busy} onClick={() => tick.mutate()} aria-label={`Tick ${b.label} for @${a.username}`}>
            <span className="igs-day-mark" />
            <span className="igs-day-label">{b.label}</span>
            <span className="igs-day-date">{readOnly ? "Next" : "Tick when done"}</span>
          </button>
        </li>
      );
    }
    return (
      <li key={b.key} className="igs-day later" aria-label={`${b.label}, not yet`}>
        <span className="igs-day-mark" />
        <span className="igs-day-label">{b.label}</span>
        <span className="igs-day-date">&nbsp;</span>
      </li>
    );
  };

  const linkBox = () => {
    if (a.linkAddedOn) {
      const label = a.highlightOn ? "Highlight" : a.linkFlaggedOn ? "Flagged" : "Added";
      return (
        <li>
          <button
            type="button"
            className={`igs-day done${a.linkFlaggedOn ? " flagged" : ""}`}
            disabled={busy}
            title="Untick"
            aria-label={a.highlightOn ? "Link moved to a highlight. Untick it" : a.linkFlaggedOn ? "Link flagged. Untick the flag" : "Link added. Untick it"}
            onClick={async () => {
              const ok = await confirm(
                a.highlightOn
                  ? { title: "Untick the highlight?", body: "The account goes back to having a flagged link to move.", confirmLabel: "Untick" }
                  : a.linkFlaggedOn
                    ? { title: "Untick the flag?", body: "The account goes back to having its link in the bio.", confirmLabel: "Untick" }
                    : { title: "Untick the link?", body: "This only changes the record here. The link itself stays in Links.", confirmLabel: "Untick" },
              );
              if (ok) unlink.mutate();
            }}
          >
            <span className="igs-day-mark"><LinkIcon size={16} /></span>
            <span className="igs-day-label">{label}</span>
            <span className="igs-day-date">{shortDate(a.highlightOn ?? a.linkFlaggedOn ?? a.linkAddedOn)}</span>
          </button>
        </li>
      );
    }
    if (live) {
      return (
        <li className="igs-day done" aria-label="Link live" title="Live when it came off the sheet">
          <span className="igs-day-mark"><LinkIcon size={16} /></span>
          <span className="igs-day-label">Live</span>
          <span className="igs-day-date">&nbsp;</span>
        </li>
      );
    }
    if (linkReady) {
      return (
        <li>
          <button type="button" className="igs-day next" disabled={busy} onClick={onLink} aria-label={`Add the link to @${a.username}'s bio`}>
            <span className="igs-day-mark"><LinkIcon size={16} /></span>
            <span className="igs-day-label">Link</span>
            <span className="igs-day-date">Add it now</span>
          </button>
        </li>
      );
    }
    return (
      <li className="igs-day later">
        <span className="igs-day-mark"><LinkIcon size={16} /></span>
        <span className="igs-day-label">Link</span>
        <span className="igs-day-date">&nbsp;</span>
      </li>
    );
  };

  return (
    <div className="igs-track">
      {element}
      <div className="igs-track-group">
        <span className="igs-track-name">{a.warmupSkipped ? "Warm-up not needed" : `${a.warmup}-day warm-up`}</span>
        <ol>{warmup.map(box)}</ol>
      </div>
      <div className="igs-track-group">
        <span className="igs-track-name">Posting, no link</span>
        <ol>{posting.map(box)}</ol>
      </div>
      <div className="igs-track-group">
        <span className="igs-track-name">Link in bio</span>
        <ol>{linkBox()}</ol>
      </div>
    </div>
  );
}
